import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import {connect} from 'react-redux';
import ReactTooltip from 'react-tooltip';

import {
  fetchOccupancyReportForRoomPurposes,
  selectOccupancyForRoomPurposes,
  selectOccupancyForRoomPurposesLoading,
  selectOccupancyForRoomPurposesError,
  UNITS_CHOICES,
  RESOLUTION_OPTIONS,
  VIEW_CHOICES,
  REPORT_SHAPE
} from 'ducks/occupancy';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {exportXlsxInBackground, selectXlsxExporting} from 'ducks/files';

import {exportOccupancyReport} from 'sdk/occupancy';

import {
  formatDate,
  formatDateForApi,
  DATE_FORMAT,
  DATE_FORMAT_API
} from 'utils/datetime';

import downloadIcon from 'assets/icons/download.svg';

import Notice from 'components/Notice';
import Loader from 'components/Loader';

import {
  RoomPurposeOverviewSectionHeader,
  RoomPurposeOverviewChart
} from './components';
import {OCCUPANCY_TYPE_CHOICES} from 'ducks/occupancy';

import './styles.css';

const percentUnitsRenderer = (currentValue, maxValue) =>
  maxValue === 0 ? '0.00%' : `${_.round(currentValue / maxValue * 100, 2)}%`;
const numberUnitsRenderer = currentValue => _.round(currentValue, 2);
const unitsRenderers = {
  [UNITS_CHOICES.PERCENT]: percentUnitsRenderer,
  [UNITS_CHOICES.NUMBERS]: numberUnitsRenderer
};

const REPORT_EXPORT_FILENAME = 'occupancy';

class RoomPurposeOverviewSection extends React.Component<any> {
  defaultResolution = RESOLUTION_OPTIONS.MONTHLY;
  defaultView = VIEW_CHOICES.OCCUPIED;

  state = {
    unitsRenderer: UNITS_CHOICES.PERCENT,
    openedNestedGroups: [],
    startDate: this.defaultResolution.defaultStartDate,
    endDate: this.defaultResolution.defaultEndDate,
    resolution: this.defaultResolution,
    firstPeriodChange: false,
    shouldShowOverviewChart: true,
    tooBigResolutionPeriod: false,
    view: this.defaultView
  };

  componentDidMount() {
    this.fetchOccupancyReportForRoomPurposes();
  }

  hasTooBigResolutionPeriod = () => {
    const {startDate, endDate, resolution} = this.state;

    const isDailyResolution = resolution === RESOLUTION_OPTIONS.DAILY;
    const monthDifference = moment(endDate, DATE_FORMAT).diff(
      moment(startDate, DATE_FORMAT),
      'months'
    );

    return isDailyResolution && monthDifference > 0;
  };

  fetchOccupancyReportForRoomPurposes = () => {
    const {locationRef} = this.props;
    const {startDate, endDate, resolution} = this.state;

    const {loading, error} = this.props;
    const hasTooBigResolutionPeriod = this.hasTooBigResolutionPeriod();

    this.setState({
      shouldShowOverviewChart: !(loading || error || hasTooBigResolutionPeriod),
      tooBigResolutionPeriod: hasTooBigResolutionPeriod
    });

    if (hasTooBigResolutionPeriod) {
      return;
    }

    this.props.fetchOccupancyReportForRoomPurposes({
      locationRef,
      periodStart: formatDateForApi(startDate),
      periodEnd: formatDateForApi(endDate),
      resolution: resolution && resolution.value
    });
  };

  toggleUnitsRenderer = value => {
    if (_.get(UNITS_CHOICES, value, false)) {
      throw new Error(`${value} is not a valid units choice`);
    }
    this.setState({unitsRenderer: value});
  };

  toggleShapeRenderer = value => {
    if (_.get(REPORT_SHAPE, value, false)) {
      throw new Error(`${value} is not a valid occupied shape choice`);
    }
    if (value.key !== 'roomPurpose') {
      this.props.toggleOverviewSectionRenderer(value);
    }
  };

  getUnitsRenderer = () => unitsRenderers[this.state.unitsRenderer];

  onResolutionChange = newResolution => {
    if (newResolution === this.state.resolution) {
      return;
    }

    // Sets the default periods only if the period hasn't been changed
    if (!this.state.firstPeriodChange) {
      this.setState(
        {
          resolution: newResolution,
          startDate: newResolution.defaultStartDate,
          endDate: newResolution.defaultEndDate
        },
        this.fetchOccupancyReportForRoomPurposes
      );
    } else {
      this.setState(
        {resolution: newResolution},
        this.fetchOccupancyReportForRoomPurposes
      );
    }
  };

  onPeriodChange = (field, value) =>
    this.setState(
      {[field]: value, firstPeriodChange: true},
      this.fetchOccupancyReportForRoomPurposes
    );

  exportReport = () => {
    const {locationRef} = this.props;
    const {startDate, endDate, resolution} = this.state;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.EXPORT_OCCUPANCY_REPORT
      },
      () => {
        const exportData = {
          locationRef,
          periodStart: formatDateForApi(startDate),
          periodEnd: formatDateForApi(endDate),
          resolution: resolution && resolution.value
        };

        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportOccupancyReport,
          fileName: REPORT_EXPORT_FILENAME
        });
      }
    );
  };

  getChartData = () => {
    const {occupancy, activeTab} = this.props;

    const roomPurposeToLabel = {
      longStay: 'Long Stay',
      shortStay: 'Short Stay',
      corporate: 'Corporate',
      guest: 'Guest',
      extendedStay: 'Extended Stay'
    };

    const tableHeaderData = Object.keys(
      _.get(occupancy, '[0].report.roomPurposeBreakdown', {})
    )
      .filter(key => key != 'totalRooms')
      .map(roomPurpose => ({
        value: _.get(
          occupancy,
          '[0].report.roomPurposeBreakdown.${roomPurpose}.real', // Only real?
          0
        ),
        ref: roomPurpose,
        label: _.get(roomPurposeToLabel, roomPurpose, roomPurpose),
        totalRooms: _.get(
          occupancy,
          `[0].report.roomPurposeBreakdown.${roomPurpose}.totalRooms`,
          (0).toFixed(2)
        ).toFixed(2),
        totalRoomsDate: formatDate(
          moment(_.get(occupancy, `[0].date`), DATE_FORMAT_API)
        )
      }));

    const elementsList = occupancy.map(dateReport => {
      const roomPurposeBreakdown = _.mapValues(
        dateReport.report.roomPurposeBreakdown,
        (v, k) => {
          if (
            activeTab == OCCUPANCY_TYPE_CHOICES.REVENUE_OCCUPANCY &&
            k == 'corporate'
          ) {
            return {
              value: v.totalRooms,
              maxValue: v.totalRooms
            };
          }
          return {
            value: v.real,
            maxValue: v.totalRooms
          };
        }
      );

      delete roomPurposeBreakdown['totalRooms']; //remove the 'total rooms key'
      return {
        date: moment(dateReport.date, DATE_FORMAT_API),
        totalRooms: dateReport.report.total.totalRooms.toFixed(2),
        totalOccupancy: dateReport.report.total.real.toFixed(2), // Total dummy?
        roomPurposeBreakdown: roomPurposeBreakdown
      };
    });
    return {elementsList, tableHeaderData};
  };

  render() {
    const {loading, exporting, error, locationRef} = this.props;
    const {
      startDate,
      endDate,
      resolution,
      view,
      shouldShowOverviewChart,
      tooBigResolutionPeriod
    } = this.state;
    const {elementsList, tableHeaderData} = this.getChartData();

    return (
      <div className="RoomPurposeOverviewSection colab-medium-border-radius">
        <RoomPurposeOverviewSectionHeader
          toggleUnitsRenderer={this.toggleUnitsRenderer}
          onResolutionChange={this.onResolutionChange}
          onPeriodChange={this.onPeriodChange}
          exportReport={this.exportReport}
          error={error}
          loading={loading}
          startDate={startDate}
          endDate={endDate}
          resolution={resolution}
          exporting={exporting}
        />
        {tooBigResolutionPeriod && (
          <Notice
            noticeIcon={downloadIcon}
            noticeText={`
              Unfortunately we cannot display a date range this far into the future,
              you can however download a CSV of this data
              `}
            noticeAction={this.exportReport}
            noticeActionText="Click to download your CSV"
          />
        )}
        {loading && <Loader />}
        {shouldShowOverviewChart && (
          <RoomPurposeOverviewChart
            elementsList={elementsList}
            tableHeaderData={tableHeaderData}
            disableShapeSwitch={view === VIEW_CHOICES.AVAILABLE}
            toggleShapeRenderer={this.toggleShapeRenderer}
            unitsRenderer={this.getUnitsRenderer()}
            resolution={resolution}
            locationRef={locationRef}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const occupancy = selectOccupancyForRoomPurposes(state);

  ReactTooltip.rebuild();

  return {
    occupancy,
    loading: selectOccupancyForRoomPurposesLoading(state),
    error: _.get(selectOccupancyForRoomPurposesError(state), '_error'),
    exporting: selectXlsxExporting(state, REPORT_EXPORT_FILENAME)
  };
};

const mapDispatchToProps = {
  fetchOccupancyReportForRoomPurposes,
  exportXlsxInBackground,
  requiresPermission
};

export default connect(mapStateToProps, mapDispatchToProps)(
  RoomPurposeOverviewSection
);
